import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ROUTES } from "@/constants/routes";
import { authStore } from "@/stores/AuthStore";
import AppLayout from "@/components/Layout/AppLayout/AppLayout";
import {
  AuthPhonePage,
  AuthCodePage,
  DashboardPage,
  PayoutsPage,
  ContractorsPage,
  ContractorDetailPage,
  ClientsPage,
  ClientDetailPage,
  FeedbackPage,
  FeedbackSettingsPage,
  GasStationsPage,
  HelpPage,
  HelpSettingsPage,
  NotFound,
} from "@/pages";

const AuthGuard = observer(() => {
  if (!authStore.isAuthenticated) {
    return <Navigate to={ROUTES.AUTH_PHONE} replace />;
  }
  return <Outlet />;
});

const PrivateRoute = observer(({ children }: { children: React.ReactNode }) => {
  if (!authStore.isAuthenticated) {
    return <Navigate to={ROUTES.AUTH_PHONE} replace />;
  }
  return <AppLayout>{children}</AppLayout>;
});

const AppRouter = observer(() => {
  return (
    <Routes>
      {/* Redirect root to contractors */}
      <Route path="/" element={<Navigate to={ROUTES.CONTRACTORS} replace />} />

      {/* Public auth routes */}
      <Route path={ROUTES.AUTH_PHONE} element={<AuthPhonePage />} />
      <Route path={ROUTES.AUTH_CODE} element={<AuthCodePage />} />

      {/* Protected routes with layout */}
      <Route element={<AuthGuard />}>
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.PAYOUTS}
          element={
            <PrivateRoute>
              <PayoutsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTRACTORS}
          element={
            <PrivateRoute>
              <ContractorsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTRACTOR_DETAIL}
          element={
            <PrivateRoute>
              <ContractorDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CLIENTS}
          element={
            <PrivateRoute>
              <ClientsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CLIENT_DETAIL}
          element={
            <PrivateRoute>
              <ClientDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.GAS_STATIONS}
          element={
            <PrivateRoute>
              <GasStationsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.FEEDBACK}
          element={
            <PrivateRoute>
              <FeedbackPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.FEEDBACK_SETTINGS}
          element={
            <PrivateRoute>
              <FeedbackSettingsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.HELP}
          element={
            <PrivateRoute>
              <HelpPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.HELP_SETTINGS}
          element={
            <PrivateRoute>
              <HelpSettingsPage />
            </PrivateRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
      <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
    </Routes>
  );
});

AppRouter.displayName = "AppRouter";

export default AppRouter;
